import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Card,
  CircularProgress,
  IconButton,
  MenuItem,
  Select,
  Tooltip,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import ArrowDownwardOutlinedIcon from "@mui/icons-material/ArrowDownwardOutlined";
import ArrowUpwardOutlinedIcon from "@mui/icons-material/ArrowUpwardOutlined";
import HearingOutlinedIcon from "@mui/icons-material/HearingOutlined";
import DeleteForeverIcon from "@mui/icons-material/DeleteForever";
import NavigateBeforeOutlinedIcon from "@mui/icons-material/NavigateBeforeOutlined";
import NavigateNextOutlinedIcon from "@mui/icons-material/NavigateNextOutlined";
import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { setActiveCalls } from "context";
import { mcx } from "globals/endpoints";
import CTLNotification from "application/components/CTLNotification";
import AuthenticationService from "../authentication/AuthenticationService";

const formatDateTime = (value) => {
  if (value === null || value === undefined || value === "") {
    return "--";
  }

  let parsedDate;

  if (typeof value === "number" || /^\d+$/.test(String(value))) {
    const rawValue = Number(value);
    const timestamp = String(value).length === 10 ? rawValue * 1000 : rawValue;
    parsedDate = new Date(timestamp);
  } else {
    const normalizedValue =
      typeof value === "string" ? value.replace(" ", "T") : value;
    parsedDate = new Date(normalizedValue);
  }

  if (Number.isNaN(parsedDate.getTime())) {
    return "--";
  }

  return parsedDate.toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  });
};

function getStatusMeta(statusValue, theme) {
  const status = String(statusValue || "").trim().toLowerCase();

  if (status === "streamack") {
    return {
      label: "Live",
      color: theme.palette.success.main,
      bg: alpha(theme.palette.success.main, 0.16),
    };
  }

  if (status === "ringing" || status === "connecting") {
    return {
      label: "Connecting",
      color: theme.palette.warning.main,
      bg: alpha(theme.palette.warning.main, 0.16),
    };
  }

  return {
    label: statusValue || "Unknown",
    color: theme.palette.info.main,
    bg: alpha(theme.palette.info.main, 0.16),
  };
}




export default function ActiveCallsList({ resolveContactIdentityByNumber }) {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortBy, setSortBy] = useState("startEpoch");
  const [sortDirection, setSortDirection] = useState("desc");
  const [currentPage, setCurrentPage] = useState(0);
  const pageSize = 10;
  const [totalPages, setTotalPages] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const theme = useTheme();
  const [controller, dispatch] = useMaterialUIController();
  const { darkMode, searchText } = controller;
  const [chatNotify, setChatNotify] = useState({
    isOpen: false,
    message: "",
    type: "",
    pagename: "Active Calls",
    status: "",
  });
  const containerRef = useRef(null);
  const listBodyRef = useRef(null);
  const [width, setWidth] = useState(300);
  const departmentList = AuthenticationService.getDepartmentList();
  // 🔹 Resize Observer
  useEffect(() => {
    const resize = () => {
      if (containerRef.current) {
        setWidth(containerRef.current.clientWidth);
      }
    };

    resize();

    const observer = new ResizeObserver(resize);
    if (containerRef.current) observer.observe(containerRef.current);

    return () => observer.disconnect();
  }, []);

  const columnCount = useMemo(() => {
    if (width >= 660) return 3;
    if (width >= 440) return 2;
    return 1;
  }, [width]);

  useEffect(() => {
    const timerId = window.setTimeout(() => {
      setDebouncedSearch((searchText || "").trim());
      setCurrentPage(0);
    }, 350);

    return () => window.clearTimeout(timerId);
  }, [searchText]);

  useEffect(() => {
    setCurrentPage(0);
  }, [sortBy, sortDirection]);

  useEffect(() => {
    let interval;
    let isMounted = true;

    const fetchCalls = async (showLoader = false) => {
      if (showLoader && isMounted) {
        setLoading(true);
      }
      
      try {
        const response = await mcx.post("broadcast/listpage", {
          currentPage,
          pageSize,
          sortBy,
          sortDirection,
          search: debouncedSearch,
          conferenceNumber: departmentList?.map((dept) => dept.extension).join(","),
        });

        const pageData = response?.data?.data || {};
        const content = Array.isArray(pageData?.content)
          ? pageData.content
          : [];
        const mappedContent = content
          .map((call) => ({
            id: call?.id,
            streamID: call?.streamID || call?.streamId || "",
            sentFrom: call?.sentFrom || "",
            sentTo: call?.sentTo || "",
            startEpoch: call?.startEpoch || call?.startTime || null,
            status: call?.status || "Unknown",
            active: Boolean(call?.active),
          }))
          .filter((call) => call.active && call.status !== "Disconnected");

        if (!isMounted) {
          return;
        }

        // ✅ Avoid unnecessary re-render
        setCalls((prev) =>
          JSON.stringify(prev) !== JSON.stringify(mappedContent)
            ? mappedContent
            : prev,
        );

        setTotalRecords(mappedContent.length);
        setTotalPages(
          mappedContent.length > 0
            ? Math.ceil(mappedContent.length / pageSize)
            : 0,
        );
      } catch {
        if (isMounted) {
          setCalls([]);
          setTotalPages(0);
          setTotalRecords(0);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchCalls(true); // initial/criteria call
    interval = setInterval(() => fetchCalls(false), 3000);

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, [currentPage, pageSize, debouncedSearch, sortBy, sortDirection]);

  // 🔹 Join Call
  const handleJoin = (call) => {
    setActiveCalls(dispatch, {
      streamID: call?.streamID || call?.streamId || "",
      sentFrom: call?.sentFrom || "",
      sentTo: call?.sentTo || "",
    });
  };

  const handleClearClick = useCallback((call) => {
    const streamID = call?.streamID || call?.streamId || "";

    if (!streamID) {
      setChatNotify({
        isOpen: true,
        message: "Invalid stream ID",
        type: "warning",
        pagename: "Active Calls",
        status: "warning",
      });
      return;
    }

    mcx.post(`broadcast/deleteCallHistory/${streamID}`)
      .then((response) => {
        if (response.data?.status == 0) {
          setCalls((prev) => prev.filter((c) => c.callKey !== call.callKey));
          setChatNotify({
            isOpen: true,
            status: response.data?.message || "Call cleared successfully",
            type: "success",
            pagename: "Active Calls",
            // status: "success",
          });
        } else {
          setChatNotify({
            isOpen: true,
            status: response.data?.message || "Failed to clear call",
            type: "error",
            pagename: "Active Calls",
            // status: "error",
          });
        }
      })
      .catch((error) => {
        setChatNotify({
          isOpen: true,
          status: error?.response?.data?.message || error?.message || "An error occurred while clearing the call",
          type: "error",
          pagename: "Active Calls",
          //status: "error",
        });
      });
  }, []);

  const isUUID = (str) => {
    const uuidRegex =
      /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
    return uuidRegex.test(str);
  };

  const canGoPrevious = currentPage > 0;
  const canGoNext = totalPages > 0 && currentPage < totalPages - 1;

  const resolveLabel = useCallback((number) => {
    if (typeof resolveContactIdentityByNumber === "function") {
      const identity = resolveContactIdentityByNumber(number);
      return identity?.designation || identity?.name || number || "--";
    }
    return number || "--";
  }, [resolveContactIdentityByNumber]);

  const isCallLive = (status) => {
    return String(status || "").trim().toLowerCase() === "streamack";
  };

  const isCallConnecting = (status) => {
    const normalized = String(status || "").trim().toLowerCase();
    return normalized === "connecting" || normalized === "ringing";
  };

  const displayCalls = useMemo(
    () =>
      calls.map((call) => {
        const isLive = isCallLive(call.status);
        const isConnecting = isCallConnecting(call.status);

        return {
          ...call,
          callKey: call.streamID || call.streamId || call.id,
          fromLabel: resolveLabel(call.sentFrom),
          toLabel: resolveLabel(call.sentTo),
          isLive,
          isConnecting,
          statusMeta: getStatusMeta(call.status, theme),
          typeLabel: isUUID(call.streamID || call.streamId)
            ? "Private Call"
            : "Group Call",
          formattedStart: formatDateTime(call.startEpoch || call.startTime),
        };
      }),
    [calls, resolveLabel, theme],
  );

  useEffect(() => {
    if (listBodyRef.current) {
      listBodyRef.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [currentPage, debouncedSearch, sortBy, sortDirection, displayCalls.length]);

  return (
    <Box
      ref={containerRef}
      sx={(theme) => ({
        height: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 0.75,
        border: `1px solid ${alpha(theme.palette.info.main, 0.18)}`,
        borderRadius: 1.4,
        p: 0.6,
        // background: darkMode
        //   ? "linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%)"
        //   : "linear-gradient(135deg, #f5f5f5 0%, #e3f2fd 50%, #f0f4ff 100%)",
      })}
    >
      {/* Header */}

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 0.6,
          flexWrap: "nowrap",
          overflowX: "auto",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          "&::-webkit-scrollbar": { display: "none" },
        }}
      >
        <Card
          sx={{
            px: 0.8,
            py: 0.15,
            borderRadius: 1.5,
            boxShadow: "none",
            flexShrink: 0,
            border: (theme) => `1px solid ${alpha(theme.palette.info.main, 0.28)}`,
            // backgroundColor: (theme) => alpha(theme.palette.info.main, 0.08),
          }}
        >
          <MDTypography
            variant="caption"
            fontWeight="bold"
            sx={{ fontSize: "0.6rem", opacity: 0.9 }}
          >
            Active Calls: {totalRecords || calls.length}
          </MDTypography>
        </Card>

        <MDTypography
          variant="caption"
          sx={{
            fontSize: "0.63rem",
            fontWeight: 600,
            opacity: 0.7,
            flexShrink: 0,
          }}
        >
          Sort By
        </MDTypography>

        <Select
          size="small"
          value={sortBy}
          onChange={(event) => setSortBy(event.target.value)}
          sx={{
            flex: 1,
            minWidth: 0,
            maxWidth: 118,
            height: 26,
            fontSize: 11,
            borderRadius: 1.5,
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: (theme) => alpha(theme.palette.info.main, 0.3),
            },
            "& .MuiSelect-select": {
              display: "flex",
              alignItems: "center",
              height: 26,
              py: 0,
              pr: 2.5,
              pl: 0.8,
            },
          }}
        >
          <MenuItem value="startEpoch" sx={{ fontSize: 12 }}>
            Latest
          </MenuItem>
          <MenuItem value="sentFrom" sx={{ fontSize: 12 }}>
            Sent From
          </MenuItem>
          <MenuItem value="sentTo" sx={{ fontSize: 12 }}>
            Sent To
          </MenuItem>
        </Select>

        <Tooltip
          title={sortDirection === "desc" ? "Descending" : "Ascending"}
        >
          <IconButton
            size="small"
            onClick={() =>
              setSortDirection((previous) =>
                previous === "desc" ? "asc" : "desc",
              )
            }
            sx={{
              width: 26,
              height: 26,
              flexShrink: 0,
              border: (theme) =>
                `1px solid ${alpha(theme.palette.info.main, 0.35)}`,
              borderRadius: 1.5,
              transition: "all 0.2s",
              "&:hover": {
                backgroundColor: (theme) => alpha(theme.palette.info.main, 0.12),
                transform: "scale(1.1)",
              },
            }}
          >
            {sortDirection === "desc" ? (
              <ArrowDownwardOutlinedIcon sx={{ fontSize: 13 }} color="info" />
            ) : (
              <ArrowUpwardOutlinedIcon sx={{ fontSize: 13 }} color="info" />
            )}
          </IconButton>
        </Tooltip>

        <MDTypography variant="caption" sx={{ opacity: 0.5, fontSize: "0.58rem", flexShrink: 0 }}>
          Auto refresh: 3s
        </MDTypography>
      </Box>
      {/* Body */}
      <Box
        ref={listBodyRef}
        sx={(theme) => ({
          flex: 1,
          overflowY: "auto",
          overflowX: "hidden",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
          borderRadius: 1.2,
          border: `1px solid ${alpha(theme.palette.info.main, 0.16)}`,
          p: 0.5,
          scrollBehavior: "smooth",
          "&::-webkit-scrollbar": {
            display: "none",
          },
        })}
      >
        {loading ? (
          <Box sx={{ textAlign: "center", mt: 3 }}>
            <CircularProgress size={22} />
          </Box>
        ) : calls.length === 0 ? (
          <Box sx={{ textAlign: "center", mt: 3 }}>
            <MDTypography variant="caption">No active calls</MDTypography>
          </Box>
        ) : (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: `repeat(${columnCount}, 1fr)`,
              gap: 0.55,
            }}
          >
            {displayCalls.map((call) => (
              <Card
                key={call.callKey}
                sx={(theme) => ({
                  p: { xs: 0.72, sm: 0.78 },
                  borderRadius: 1.5,
                  minHeight: 60,
                  borderLeft: `3px solid ${call.statusMeta.color}`,
                  border: `1px solid ${alpha(
                    darkMode
                      ? theme.palette.info.light
                      : theme.palette.info.main,
                    darkMode ? 0.25 : 0.18,
                  )}`,
                  boxShadow: `0 3px 12px ${alpha(call.statusMeta.color, darkMode ? 0.28 : 0.2)}`,
                  transition: "all 0.2s ease",
                  "&:hover": {
                    transform: "translateY(-1px)",
                    boxShadow: `0 8px 18px ${alpha(call.statusMeta.color, darkMode ? 0.38 : 0.28)}`,
                  },
                })}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 1,
                    width: "100%",
                  }}
                >
                  {/* Info: 3 rows */}
                  <Box sx={{ display: "grid", gap: 0.4, flex: 1, minWidth: 0 }}>
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        gap: 0.75,
                      }}
                    >
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "auto minmax(0, 1fr)",
                          alignItems: "center",
                          gap: 0.45,
                          minWidth: 0,
                          flex: 1,
                        }}
                      >
                        <PersonOutlineIcon
                          sx={{
                            fontSize: 14,
                            color: call.statusMeta.color,
                            p: 0.25,
                            borderRadius: "50%",
                            backgroundColor: alpha(call.statusMeta.color, 0.18),
                          }}
                        />

                        <MDTypography
                          variant="caption"
                          fontWeight="bold"
                          noWrap
                          sx={{ fontSize: "0.67rem", lineHeight: 1.2 }}
                        >
                          {call.fromLabel}
                          {" → "}
                          {call.toLabel}
                        </MDTypography>
                      </Box>

                      <MDTypography
                        variant="caption"
                        sx={{
                          fontSize: "0.58rem",
                          opacity: 0.82,
                          px: 0.55,
                          py: 0.1,
                          borderRadius: 1,
                          color: call.statusMeta.color,
                          backgroundColor: alpha(call.statusMeta.color, 0.12),
                          border: `1px solid ${alpha(call.statusMeta.color, 0.28)}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {call.typeLabel}
                      </MDTypography>
                    </Box>

                    <Box
                      sx={{
                        height: "1px",
                        mx: -0.72,
                        my: 0.05,
                        background: (theme) =>
                          `linear-gradient(90deg, transparent, ${alpha(theme.palette.divider, 0.65)}, transparent)`,
                      }}
                    />

                    {/* Row 2: status chip + datetime */}
                    <Box
                      sx={{
                        display: "grid",
                        gridTemplateColumns: "auto minmax(0, 1fr)",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Box
                        sx={{
                          px: 0.5,
                          py: 0.18,
                          borderRadius: 0.8,
                          display: "inline-flex",
                          alignItems: "center",
                          gap: 0.35,
                          backgroundColor: call.statusMeta.bg,
                          border: `1px solid ${alpha(call.statusMeta.color, 0.28)}`,
                          flexShrink: 0,
                        }}
                      >
                        <Box
                          sx={{
                            width: 6,
                            height: 6,
                            borderRadius: "50%",
                            backgroundColor: call.statusMeta.color,
                            flexShrink: 0,
                          }}
                        />
                        <MDTypography
                          variant="caption"
                          sx={{
                            fontSize: "0.67rem",
                            fontWeight: 600,
                            lineHeight: 1,
                            color: call.statusMeta.color,
                            whiteSpace: "nowrap",
                          }}
                        >
                          {call.statusMeta.label}
                        </MDTypography>
                      </Box>

                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.3,
                          minWidth: 0,
                        }}
                      >
                        <MDTypography
                          variant="caption"
                          noWrap
                          sx={{
                            fontSize: "0.62rem",
                            opacity: 0.8,
                            lineHeight: 1.2,
                          }}
                        >
                          {call.formattedStart}
                        </MDTypography>
                      </Box>
                    </Box>
                  </Box>

                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.45 }}>
                    <Tooltip
                      title={
                        call.isLive
                          ? "Barge-In"
                          : call.isConnecting
                            ? "Call is connecting"
                            : "Wait for go live"
                      }
                    >
                      <span>
                        <IconButton
                          size="small"
                          disabled={!call.isLive}
                          onClick={() => handleJoin(call)}
                          sx={{
                            width: 34,
                            height: 34,
                            flexShrink: 0,
                            borderRadius: "50%",
                            border: `1px solid ${alpha(call.statusMeta.color, 0.5)}`,
                            backgroundColor: alpha(call.statusMeta.color, 0.16),
                            boxShadow: `0 3px 10px ${alpha(call.statusMeta.color, 0.28)}`,
                            transition: "all 0.18s",
                            "&:hover": {
                              backgroundColor: alpha(call.statusMeta.color, 0.28),
                              transform: "scale(1.08)",
                            },
                          }}
                        >
                          <HearingOutlinedIcon
                            sx={{ fontSize: 18 }}
                            htmlColor={call.statusMeta.color}
                          />
                        </IconButton>
                      </span>
                    </Tooltip>

                    <Tooltip title="Clear">
                      <span>
                        <IconButton
                          size="small"
                          disabled={!call.id}
                          onClick={() => handleClearClick(call)}
                          sx={{
                            width: 34,
                            height: 34,
                            flexShrink: 0,
                            borderRadius: "50%",
                            border: `1px solid ${alpha(theme.palette.error.main, 0.5)}`,
                            backgroundColor: alpha(theme.palette.error.main, 0.12),
                            transition: "all 0.18s",
                            "&:hover": {
                              backgroundColor: alpha(theme.palette.error.main, 0.22),
                              transform: "scale(1.08)",
                            },
                          }}
                        >
                          <DeleteForeverIcon sx={{ fontSize: 18 }} color="error" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
              </Card>
            ))}
          </Box>
        )}
      </Box>

      {!loading && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            px: 0.4,
            pt: 0.2,
            borderTop: (theme) => `1px solid ${alpha(theme.palette.divider, 0.5)}`,
          }}
        >
          <MDTypography variant="caption" sx={{ opacity: 0.6, fontSize: "0.6rem" }}>
            Page {totalPages === 0 ? 0 : currentPage + 1} / {totalPages}
          </MDTypography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 0.35 }}>
            <IconButton
              size="small"
              disabled={!canGoPrevious}
              onClick={() =>
                setCurrentPage((previous) => Math.max(previous - 1, 0))
              }
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                border: (theme) =>
                  `1px solid ${alpha(theme.palette.info.main, canGoPrevious ? 0.4 : 0.15)}`,
                transition: "all 0.18s",
                "&:not(:disabled):hover": {
                  backgroundColor: (theme) => alpha(theme.palette.info.main, 0.12),
                  transform: "scale(1.1)",
                },
              }}
            >
              <NavigateBeforeOutlinedIcon sx={{ fontSize: 14 }} color="text" />
            </IconButton>

            <IconButton
              size="small"
              disabled={!canGoNext}
              onClick={() =>
                setCurrentPage((previous) =>
                  totalPages > 0
                    ? Math.min(previous + 1, totalPages - 1)
                    : previous,
                )
              }
              sx={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                border: (theme) =>
                  `1px solid ${alpha(theme.palette.info.main, canGoNext ? 0.4 : 0.15)}`,
                transition: "all 0.18s",
                "&:not(:disabled):hover": {
                  backgroundColor: (theme) => alpha(theme.palette.info.main, 0.12),
                  transform: "scale(1.1)",
                },
              }}
            >
              <NavigateNextOutlinedIcon sx={{ fontSize: 14 }} color="text" />
            </IconButton>
          </Box>
        </Box>
      )}
      <CTLNotification notify={chatNotify} setNotify={setChatNotify} />
    </Box>
  );
}
